import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const OPTIONS = [
  { key: "credit", label: "Use 1 credit", hint: "taken from the player's credit balance" },
  { key: "invoice", label: "Send payment invoice", hint: "booking shows unpaid and they're emailed a pay link" },
  { key: "free", label: "Re-add free", hint: "no charge" },
];

// Coach/admin manual re-add of a cancelled player, choosing how payment is handled.
export function ReAddButton({ sessionId, c, full, price = 0, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [mode, setMode] = useState("free");
  const [busy, setBusy] = useState(false);
  const opts = OPTIONS.filter((o) => o.key !== "invoice" || Number(price) > 0);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/offerings/${sessionId}/re-add/${c.id}`, { payment: mode });
      toast.success({ free: `${c.name} re-added — no charge`, credit: `${c.name} re-added — 1 credit used`,
        invoice: `${c.name} re-added — payment link emailed` }[mode]);
      setConfirming(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't re-add"); }
    finally { setBusy(false); }
  };
  if (full) return <span className="text-[11px] uppercase tracking-wider text-[#737373]" data-testid={`cancelled-readd-full-${c.id}`}>Session full</span>;
  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} data-testid={`cancelled-readd-${c.id}`}
        className="min-h-[36px] px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors inline-flex items-center gap-1.5">
        <UserPlus className="w-3.5 h-3.5" /> Manual re-add
      </button>
    );
  }
  return (
    <div className="w-full sm:w-auto space-y-2 text-xs border border-[#262626] p-3" data-testid={`cancelled-readd-confirm-${c.id}`}>
      <div className="font-semibold">Re-add {c.name} — how should payment be handled?</div>
      {opts.map((o) => (
        <label key={o.key} className="flex items-start gap-2 cursor-pointer" data-testid={`cancelled-readd-opt-${o.key}-${c.id}`}>
          <input type="radio" name={`readd-${c.id}`} checked={mode === o.key} onChange={() => setMode(o.key)} className="mt-0.5 accent-[#CCFF00]" />
          <span><span className="text-white">{o.label}{o.key === "invoice" ? ` (£${Number(price).toFixed(2)})` : ""}</span> <span className="text-[#737373]">— {o.hint}</span></span>
        </label>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <button type="button" onClick={go} disabled={busy} data-testid={`cancelled-readd-yes-${c.id}`}
          className="px-3 py-1.5 uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-1 disabled:opacity-50">
          {busy && <Loader2 className="w-3 h-3 animate-spin" />} Re-add
        </button>
        <button type="button" onClick={() => setConfirming(false)} data-testid={`cancelled-readd-no-${c.id}`}
          className="px-2 py-1.5 uppercase tracking-wider text-[#A3A3A3] hover:text-white">Back</button>
      </div>
    </div>
  );
}
